import { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@rsos/assets/icons';
import type { PreconfiguredResponse } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesAPI';
import DeleteMessageModal from '../modals/DeleteMessageModal';
import ManageMessageModal from '../modals/ManageMessageModal';
import {
  EmptyState,
  NoResultsState,
  TableError,
  TableLoading,
} from '../tableShared';
import styles from './PreconfiguredMessagesTable.module.css';
import TableRow from './TableRow';

const TABLE_HEIGHT = 312;

type SortDir = 'asc' | 'desc';

interface Props {
  messages: PreconfiguredResponse[];
  searchTerm: string;
  isLoading: boolean;
  isError: boolean;
}

const PreconfiguredMessagesTable = ({
  messages,
  searchTerm,
  isLoading,
  isError,
}: Props) => {
  const [selectedMessage, setSelectedMessage] =
    useState<PreconfiguredResponse | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleTitleSort = () => {
    setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const sortedMessages = [...messages].sort((a, b) => {
    const cmp = (a.title ?? '').localeCompare(b.title ?? '');
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const handleEdit = (message: PreconfiguredResponse) => {
    setSelectedMessage(message);
    setIsManageModalOpen(true);
  };

  const handleDelete = (message: PreconfiguredResponse) => {
    setSelectedMessage(message);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteClose = () => {
    setIsDeleteModalOpen(false);
    setSelectedMessage(null);
  };

  const handleManageClose = () => {
    setIsManageModalOpen(false);
    setSelectedMessage(null);
  };

  if (isLoading) return <TableLoading height={TABLE_HEIGHT} />;
  if (isError) return <TableError height={TABLE_HEIGHT} />;

  return (
    <>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>
                <button
                  type="button"
                  className={styles.sortButton}
                  onClick={handleTitleSort}
                  aria-label={`Sort by message title${sortDir === 'asc' ? ', ascending' : sortDir === 'desc' ? ', descending' : ''}`}
                >
                  Message Title
                  <span className={styles.sortIndicator} aria-hidden="true">
                    {sortDir === 'asc' ? (
                      <ChevronUpIcon width={12} height={12} />
                    ) : (
                      <ChevronDownIcon width={12} height={12} />
                    )}
                  </span>
                </button>
              </th>
              <th className={styles.th}>Message Body</th>
              <th className={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedMessages.length === 0 ? (
              <tr>
                <td className={styles.emptyCell} colSpan={3}>
                  {searchTerm ? <NoResultsState /> : <EmptyState />}
                </td>
              </tr>
            ) : (
              sortedMessages.map(message => (
                <TableRow
                  key={message.id}
                  message={message}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      {isDeleteModalOpen && selectedMessage && (
        <DeleteMessageModal
          isOpened={isDeleteModalOpen}
          onClose={handleDeleteClose}
          message={selectedMessage}
        />
      )}
      {isManageModalOpen && selectedMessage && (
        <ManageMessageModal
          isOpened={isManageModalOpen}
          onClose={handleManageClose}
          message={selectedMessage}
          isEdit
        />
      )}
    </>
  );
};

export default PreconfiguredMessagesTable;
